import { cn } from "@/lib/utils";
import type { FpMessage, FpMessagePart } from "../../../../schemas";
import { Avatar } from "../Avatar";
import { Markdown } from "../Markdown";
import { ResponseFeedback } from "../ResponseFeedback";
import { TypingIndicator } from "../TypingIndicator";
import styles from "./MessageContent.module.css";
import { MessageParts } from "./MessageParts";
import { RetryButton } from "./RetryButton";
import { UserAttachments } from "./UserAttachments";

const syntaxHighlighterOptions = {
  className: "rounded-md p-4 bg-muted",
};

type EphemeralMessageContentProps = {
  type: "ephemeral";
  id: string;
  sender: "user";
  content: FpMessage["content"];
  status: FpMessage["status"];
  isCancelled: boolean;
  hasUserAttachments: boolean;
  originalMessage?: string;
  usesParts?: boolean;
  hasPartsContent?: boolean;
  parts?: FpMessagePart[];
  isWaitingForParts?: boolean;
  isAgentThinking?: boolean;
  isFinalMessage?: boolean;
  hasSystemMessage?: boolean;
};

type PersistedMessageContentProps = {
  type: "persisted";
  id: string;
  sender: FpMessage["sender"];
  content: FpMessage["content"];
  status: FpMessage["status"];
  isCancelled: boolean;
  hasUserAttachments: boolean;
  originalMessage?: string;
  projectId: string;
  usesParts?: boolean;
  hasPartsContent?: boolean;
  parts?: FpMessagePart[];
  isWaitingForParts?: boolean;
  isAgentThinking?: boolean;
  isFinalMessage?: boolean;
  hasSystemMessage?: boolean;
};

const EMPTY_PARTS: FpMessagePart[] = [];

export function MessageContent(
  props: PersistedMessageContentProps | EphemeralMessageContentProps,
) {
  const {
    id,
    sender,
    content,
    status,
    isCancelled,
    isAgentThinking,
    hasUserAttachments,
    originalMessage,
    type,
    usesParts = false,
    hasPartsContent = false,
    isWaitingForParts = false,
    parts = EMPTY_PARTS,
    isFinalMessage = false,
    hasSystemMessage = false,
  } = props;

  const isEmpty = usesParts
    ? !hasPartsContent && !hasUserAttachments && !isWaitingForParts
    : !content && !hasUserAttachments;

  return (
    <div
      className="grid grid-cols-[auto_minmax(0,1fr)] gap-[10px] [contain:content]"
      id={id}
    >
      <Avatar sender={sender} />

      <div
        className={cn(
          "self-center flex flex-col gap-3",
          isCancelled && "text-muted-foreground",
          styles.messageContent,
        )}
      >
        {!isEmpty ? (
          <>
            {/* Use parts rendering for new messages, legacy for old messages */}
            {usesParts ? (
              hasPartsContent && parts ? (
                <>
                  <MessageParts parts={parts} messageId={id} />
                  {isAgentThinking && <TypingIndicator showText={false} />}
                </>
              ) : (
                <TypingIndicator />
              )
            ) : (
              content && (
                <Markdown
                  syntaxHighlighterOptions={syntaxHighlighterOptions}
                  isStreaming={status === "processing" || status === "pending"}
                >
                  {content}
                </Markdown>
              )
            )}
            {isCancelled && !hasSystemMessage && sender === "agent" && (
              <div className="text-sm text-muted-foreground mt-2 italic">
                Message generation cancelled
              </div>
            )}
          </>
        ) : isCancelled ? (
          <div className="text-sm text-muted-foreground italic">
            Message generation cancelled
          </div>
        ) : (
          <TypingIndicator />
        )}

        {/* Show user attachments for persisted user messages */}
        {type === "persisted" && sender === "user" && (
          <UserAttachments messageId={id} projectId={props.projectId} />
        )}

        {isCancelled &&
          sender === "agent" &&
          originalMessage &&
          isFinalMessage && <RetryButton originalMessage={originalMessage} />}

        {type === "persisted" &&
          sender === "agent" &&
          !isEmpty &&
          status === "completed" && (
            <div>
              <ResponseFeedback messageId={id} projectId={props.projectId} />
            </div>
          )}
      </div>
    </div>
  );
}
